import { inject } from '@angular/core';
import { ResolveFn, ActivatedRouteSnapshot } from '@angular/router';
import { from, map, switchMap, of, Observable } from 'rxjs';
import { ApiService } from '../services/api.service';
import { HomePageDTO } from '../models/home-page-dto';
import { ReagentsPageDTO } from '../models/reagents-page-dto';
import { SystemsPageDTO } from '../models/systems-page-dto';
import { CompanyPageDTO } from '../models/company-page-dto';
import { JobsPageDTO } from '../models/jobs-page-dto';
import { SupportPageDTO } from '../models/support-page-dto';
import { ProductDTO } from '../models/product-dto';
import { SystemDTO } from '../models/system-dto';
import { ContactPageDTO } from '../models/contact-page-dto';
import { TermsAndConditionsPageDTO } from '../models/terms-and-conditions-page-dto';
import { PrivacyPolicyPageDTO } from '../models/privacy-policy-page-dto';
import { LegalNoticePageDTO } from '../models/legal-notice-page-dto';
import { NotFoundPageDTO } from '../models/not-found-page-dto';
import { ReagentsArchivePageDTO } from '../models/reagents-archive-page-dto';
import { StrapiComponent } from '../models/components/strapi-component';
import { MODULE_DEFINITION_MAP } from '../../modules/definitions/module.definitions';

function prefetchComponentsForModules(
  modules: StrapiComponent[] | undefined,
): Observable<void> {
  if (!modules || modules.length === 0) {
    return of(void 0);
  }
  const loaders = modules
    .map(m => MODULE_DEFINITION_MAP.get(m.__component))
    .filter((def): def is NonNullable<typeof def> => !!def)
    .map(def => def.load());
  return from(Promise.all(loaders)).pipe(map(() => void 0));
}

export const homePageResolver: ResolveFn<HomePageDTO> = () => {
  const api = inject(ApiService);
  return api
    .getHomePage$()
    .pipe(
      map(res => res.data),
      switchMap(data => prefetchComponentsForModules(data.modules).pipe(switchMap(() => of(data)))),
    );
};

export const reagentsPageResolver: ResolveFn<ReagentsPageDTO> = () => {
  const api = inject(ApiService);
  return api
    .getReagentsPage$()
    .pipe(
      map(res => res.data),
      switchMap(data => prefetchComponentsForModules(data.modules).pipe(switchMap(() => of(data)))),
    );
};

export const reagentsArchivePageResolver: ResolveFn<ReagentsArchivePageDTO> = () => {
  const api = inject(ApiService);
  return api
    .getReagentsArchivePage$()
    .pipe(
      map(res => res.data),
      switchMap(data => prefetchComponentsForModules(data.modules).pipe(switchMap(() => of(data)))),
    );
};

export const systemsPageResolver: ResolveFn<SystemsPageDTO> = () => {
  const api = inject(ApiService);
  return api
    .getSystemsPage$()
    .pipe(
      map(res => res.data),
      switchMap(data => prefetchComponentsForModules(data.modules).pipe(switchMap(() => of(data)))),
    );
};

export const systemDetailResolver: ResolveFn<SystemDTO> = (
  route: ActivatedRouteSnapshot,
) => {
  const api = inject(ApiService);
  const slug = route.paramMap.get('slug') as string;
  return api
    .getSystemDetails$(slug)
    .pipe(
      map(res => res.data),
      switchMap(data => prefetchComponentsForModules(data.modules).pipe(switchMap(() => of(data)))),
    );
};

export const reagentsDetailResolver: ResolveFn<ProductDTO> = (
  route: ActivatedRouteSnapshot,
) => {
  const api = inject(ApiService);
  const slug = route.paramMap.get('slug') as string;
  return api
    .getReagentsDetails$(slug)
    .pipe(
      map(res => res.data),
      switchMap(data => prefetchComponentsForModules(data.modules).pipe(switchMap(() => of(data)))),
    );
};

export const companyPageResolver: ResolveFn<CompanyPageDTO> = () => {
  const api = inject(ApiService);
  return api
    .getCompanyPage$()
    .pipe(
      map(res => res.data),
      switchMap(data => prefetchComponentsForModules(data.modules).pipe(switchMap(() => of(data)))),
    );
};

export const jobsPageResolver: ResolveFn<JobsPageDTO> = () => {
  const api = inject(ApiService);
  return api
    .getJobsPage$()
    .pipe(
      map(res => res.data),
      switchMap(data => prefetchComponentsForModules(data.modules).pipe(switchMap(() => of(data)))),
    );
};

export const supportPageResolver: ResolveFn<SupportPageDTO> = () => {
  const api = inject(ApiService);
  return api
    .getSupportPage$()
    .pipe(
      map(res => res.data),
      switchMap(data => prefetchComponentsForModules(data.modules).pipe(switchMap(() => of(data)))),
    );
};

export const contactPageResolver: ResolveFn<ContactPageDTO> = () => {
  const api = inject(ApiService);
  return api
    .getContactPage$()
    .pipe(
      map(res => res.data),
      switchMap(data => prefetchComponentsForModules(data.modules).pipe(map(() => data))),
    );
};

export const legalNoticePageResolver: ResolveFn<LegalNoticePageDTO> = () => {
  const api = inject(ApiService);
  return api
    .getLegalNoticePage$()
    .pipe(
      map(res => res.data),
      switchMap(data => prefetchComponentsForModules(data.modules).pipe(map(() => data))),
    );
};

export const privacyPolicyPageResolver: ResolveFn<PrivacyPolicyPageDTO> = () => {
  const api = inject(ApiService);
  return api
    .getPrivacyPolicyPage$()
    .pipe(
      map(res => res.data),
      switchMap(data => prefetchComponentsForModules(data.modules).pipe(map(() => data))),
    );
};

export const termsAndConditionsPageResolver: ResolveFn<TermsAndConditionsPageDTO> = () => {
  const api = inject(ApiService);
  return api
    .getTermsAndConditionsPage$()
    .pipe(
      map(res => res.data),
      switchMap(data => prefetchComponentsForModules(data.modules).pipe(map(() => data))),
    );
};

export const notFoundPageResolver: ResolveFn<NotFoundPageDTO> = () => {
  const api = inject(ApiService);
  return api
    .getNotFoundPage$()
    .pipe(
      map(res => res.data),
      switchMap(data => prefetchComponentsForModules(data.modules).pipe(map(() => data))),
    );
};
